import React, { createContext, useContext, useEffect, useRef, useState } from "react"

export type IMediaQueryContext = {
  isMobileDevice: boolean
  isTabletDevice: boolean
  isDesktopDevice: boolean
}

const defaults = {
  isMobileDevice: false,
  isTabletDevice: false,
  isDesktopDevice: true, // Default to true to avoid breaking existing implementations
}

export const MediaQueryContext = createContext<IMediaQueryContext>(defaults)

const IS_SERVER = typeof window === 'undefined'

export const useMediaQuery = () => {
  const { isMobileDevice } = useContext(MediaQueryContext)

  return { isMobileDevice }
}

export const MediaQueryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isMobileDevice, setMobileDevice] = useState(defaults.isMobileDevice)
  const [isTabletDevice, setTabletDevice] = useState(defaults.isTabletDevice)
  const [isDesktopDevice, setDesktopDevice] = useState(defaults.isDesktopDevice)

  const matchMedia = (query: string) => {
    if (IS_SERVER) return false

    const result = window.matchMedia(query)
    return result.matches
  }

  const addMetadata = (config: {
    mobile: boolean
    tablet: boolean
    desktop: boolean
  }) => {
    if (IS_SERVER) return

    const element = document.body

    if (element) {
      element.setAttribute('asktia__mobile', String(config.mobile))
      element.setAttribute('asktia__tablet', String(config.tablet))
      element.setAttribute('asktia__desktop', String(config.desktop))
    }
  }

  useEffect(() => {
    if (IS_SERVER) return

    const controller = new AbortController()

    if ('function' !== typeof window.matchMedia) return

    const mobile = matchMedia('screen and (max-width: 47rem)')
    setMobileDevice(mobile)

    const desktop = matchMedia('screen and (min-width: 64rem)')
    setDesktopDevice(mobile)

    const tablet = matchMedia('screen and (min-width: 48rem) and (max-width: 63rem)')
    setTabletDevice(tablet)

    addMetadata({ mobile, tablet, desktop })

    return () => controller.abort()
  }, [])

  return (
    <MediaQueryContext.Provider value={{ isMobileDevice, isTabletDevice, isDesktopDevice }}>
      {children}
    </MediaQueryContext.Provider>
  )
}

export default MediaQueryProvider